import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CmgUiUtilities } from 'apps/cmg-clear2-ui/src/app/cmg-1003/components/common/cmg-ui-utils';
import {
  IncomeFrequencyType,
  incomeFrequencyOptions,
} from 'apps/cmg-clear2-ui/src/app/cmg-1003/components/employment-income-expenses/income/income-calculator/income.lookups';
import { EmploymentModel, Cmg1003SectionTypes, EmploymentIncomeExpenseModel, IncomeModel } from 'apps/cmg-clear2-ui/src/app/cmg-1003/models';
import { states, countriesWithSelectOption } from 'apps/cmg-clear2-ui/src/app/cmg-1003/models/lookups/common.lookups';
import {
  EmploymentStatus,
  IncomeType,
  employmentIncomeTypes,
  ownershipShareTypes,
} from 'apps/cmg-clear2-ui/src/app/cmg-1003/models/lookups/employment.lookups';
import { ZipcodeModel } from 'apps/cmg-clear2-ui/src/app/cmg-1003/models/lookups/zip-code.model';
import { Cmg1003ConstantsService } from 'apps/cmg-clear2-ui/src/app/cmg-1003/services/cmg-1003-constants.service';
import { Cmg1003LookupsService } from 'apps/cmg-clear2-ui/src/app/cmg-1003/services/cmg-1003-lookups.service';
import { SessionStore, SessionQuery } from 'apps/cmg-clear2-ui/src/app/core/state';
import {
  Cmg1003ApplicationInfoQuery,
  Cmg1003EmploymentIncomeExpensesQuery,
  Cmg1003EmploymentIncomeExpensesStore,
  Cmg1003StoreQueryService,
} from 'apps/cmg-clear2-ui/src/app/loan-state';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  startWith,
  Subject,
  Subscription,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { Table, TableModule } from 'primeng/table';
import { Panel } from 'primeng/panel';
import { PrimeTemplate } from 'primeng/api';
import { RadioButton } from 'primeng/radiobutton';
import { CmgFormControlDomDirective } from '../../../directives/cmg-form-control-dom.directive';
import { Checkbox } from 'primeng/checkbox';
import { NgClass, CurrencyPipe } from '@angular/common';
import { InputText } from 'primeng/inputtext';
import { CmgValidatorMessagesComponent } from '../../cmg-validator-messages/cmg-validator-messages.component';
import { DatePicker } from 'primeng/datepicker';
import { CmgPrimeNgDatePickerMaskDirective } from '../../../directives/cmg-datepicker-mask.directive';
import { InputMask } from 'primeng/inputmask';
import { CmgPrimeNgInputMaskFocusDirective } from '../../../directives/cmg-input-mask-focus.directive';
import { CmgFormControlZipCodeComponent } from '../../cmg-form-control-zip-code/cmg-form-control-zip-code.component';
import { Select } from 'primeng/select';
import { InputNumber } from 'primeng/inputnumber';
import { CmgPrimeNgInputNumberFocusDirective } from '../../../directives/cmg-input-number-focus.directive';
import { Button, ButtonDirective } from 'primeng/button';
import { QaSlugPipe } from '../../../pipes/qa-slug.pipe';
import { ToNameOrValuePipe } from '../../../pipes/to-name.pipe';

/**
 * @deprecated
 * This component has been replaced a new version and can be removed when the EmploymentTableFlyoutRedesign feature flag is removed
 */
@Component({
  selector: 'add-edit-employment-income',
  templateUrl: './add-edit-employment-income.component.html',
  styleUrls: ['./add-edit-employment-income.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Panel,
    PrimeTemplate,
    ReactiveFormsModule,
    RadioButton,
    CmgFormControlDomDirective,
    Checkbox,
    InputText,
    CmgValidatorMessagesComponent,
    DatePicker,
    CmgPrimeNgDatePickerMaskDirective,
    InputMask,
    CmgPrimeNgInputMaskFocusDirective,
    CmgFormControlZipCodeComponent,
    Select,
    InputNumber,
    CmgPrimeNgInputNumberFocusDirective,
    TableModule,
    NgClass,
    FormsModule,
    Button,
    ButtonDirective,
    CurrencyPipe,
    QaSlugPipe,
    ToNameOrValuePipe,
  ],
})
export class AddEditEmploymentIncomeComponent implements OnInit, OnChanges, OnDestroy {
  private readonly _fb = inject(UntypedFormBuilder);
  private readonly _sessionStore = inject(SessionStore);
  private readonly _sessionQuery = inject(SessionQuery);
  private readonly _cmg1003ConstantsService = inject(Cmg1003ConstantsService);
  private readonly _appQuery = inject(Cmg1003ApplicationInfoQuery);
  private readonly _empIncExpQuery = inject(Cmg1003EmploymentIncomeExpensesQuery);
  private readonly _empIncExpStore = inject(Cmg1003EmploymentIncomeExpensesStore);
  private readonly _storeQuery = inject(Cmg1003StoreQueryService);
  private readonly _lookupsService = inject(Cmg1003LookupsService);

  readonly table = viewChild<Table>('dt');
  EmploymentStatus = EmploymentStatus;

  readonly isPrimaryBorrower = input<boolean>(undefined);
  readonly readOnly = input(false);

  @Input()
  id: number;
  readonly borrowerId = input<number>(undefined);

  readonly editCancelled = output();

  form: UntypedFormGroup;

  states = states;
  countries = countriesWithSelectOption;
  ownershipShareTypes = ownershipShareTypes;
  todaysDate = new Date();
  isPrimary = false;
  employmentType = null;
  incomesGridColumns: any[];
  employmentIncomes: IncomeModel[];
  incomeIds: number[];
  incomeTypes = CmgUiUtilities.sortLookup(employmentIncomeTypes, 'value');
  frequencyOptions = incomeFrequencyOptions;

  /** Prefix for QA automation IDs. */
  autoIdPrefix?: string;
  readonly minEmploymentDate = this._cmg1003ConstantsService.date200YearsAgo;
  readonly maxEmploymentDate = this.todaysDate;
  readonly empAllowedYearsRange = `${this.minEmploymentDate.getFullYear()}:${this.maxEmploymentDate.getFullYear()}`;
  readonly _readOnlySubject = new BehaviorSubject(false);
  readonly dateTimePickerCalendarZIndex = this._cmg1003ConstantsService.dateTimePickerCalendarZIndex;

  private _destroyed$: Subject<boolean> = new Subject<boolean>();
  private readonly _subscription = new Subscription();

  constructor() {
    this.form = this._fb.group(
      {
        employmentType: [],
        isPrimary: [{ value: false, disabled: false }],
        isSelfEmployment: [],
        ownershipShare: [1],
        isSpecialRelationship: [],
        employerName: [null, [Validators.required]],
        fromDate: [null, [Validators.required]],
        toDate: [],
        zip: new UntypedFormControl(null, {
          updateOn: 'blur',
        }),
        street: [],
        city: [],
        state: [null],
        country: ['US'],
        businessPhone: [],
        position: [],
        monthlyIncome: [null],
        notes: '',
        aptSuiteOther: '',
      },
      { updateOn: 'change' },
    );
  }

  ngOnInit(): void {
    const isPrimaryBorrower = this.isPrimaryBorrower();
    if (!isPrimaryBorrower) {
      this._subscription.add(
        this._appQuery.getAdditionalBorrowerId$().subscribe((t) => {
          this._onAdditionalBorrowerChanged(t);
        }),
      );
    }
    this._subscription.add(
      this.form
        .get('zip')
        .valueChanges.pipe(filter((t) => !!t))
        .subscribe((t) => {
          this._onZipcodeChanged(t);
        }),
    );
    const employmentType = this._initializeForm();

    this._subscription.add(
      combineLatest([
        this._empIncExpQuery.selectPrimaryEmploymentId(!isPrimaryBorrower),
        this.form.get('employmentType').valueChanges.pipe(startWith(employmentType)),
        this._readOnlySubject.asObservable(),
      ])
        .pipe(distinctUntilChanged())
        .subscribe((t) => {
          this._onEmploymentTypeChanged(t);
        }),
    );

    this.autoIdPrefix = isPrimaryBorrower ? 'borrowerEmployer-' : 'coBorrowerEmployer-';

    this._sessionQuery
      .select((s) => s.all1003Loaded)
      .pipe(
        filter((all1003Loaded) => all1003Loaded === true),
        debounceTime(500),
        take(1),
      )
      .subscribe(() => {
        this.form.statusChanges.pipe(debounceTime(0), takeUntil(this._destroyed$)).subscribe(() => {
          this._sessionStore.triggerRevalidation();
        });
      });

    this.incomesGridColumns = [
      { field: 'incomeId', header: '', width: '0%' },
      { field: 'incomeType', header: 'Type', width: '15%' },
      { field: 'incomeFrequencyType', header: 'Frequency', width: '15%' },
      { field: 'hoursPerWeek', header: 'Hrs/Week', width: '10%' },
      { field: 'incomeRate', header: 'Amount', width: '10%' },
      { field: 'amount', header: 'Calculated Monthly Amount', width: '25%' },
      { field: 'actionsCol', header: '', width: '15%' },
    ];
  }

  ngOnDestroy() {
    if (this._subscription && !this._subscription.closed) {
      this._subscription.unsubscribe();
    }
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    for (const propName in changes) {
      if (Object.prototype.hasOwnProperty.call(changes, propName)) {
        switch (propName) {
          case 'id':
            if (this.id !== undefined) {
              this._initializeForm();
              this._buildTable();
              CmgUiUtilities.markFormAsDirty(this.form, false);
              CmgUiUtilities.markFormAsTouched(this.form, false);
            }
            break;

          case 'readOnly': {
            if (changes['readOnly'].currentValue !== undefined) {
              const readOnly = this.readOnly();
              this._enableDisableForm(!readOnly);
              this._readOnlySubject.next(readOnly);
            }
            break;
          }
        }
      }
    }
  }

  calculateMonthlyAmount(row) {
    if (row.incomeRate) {
      if (row.incomeFrequencyType !== IncomeFrequencyType.Hourly) {
        row.hoursPerWeek = null;
      }
      switch (row.incomeFrequencyType) {
        case IncomeFrequencyType.Yearly:
          row.amount = row.incomeRate / 12;
          break;
        case IncomeFrequencyType.SemiMonthly:
          row.amount = row.incomeRate * 2.0;
          break;
        case IncomeFrequencyType.BiWeekly:
          row.amount = (row.incomeRate * 26.0) / 12;
          break;
        case IncomeFrequencyType.Weekly:
          row.amount = (row.incomeRate * 52.0) / 12;
          break;
        case IncomeFrequencyType.Hourly:
          row.amount = (row.incomeRate * (row.hoursPerWeek !== null ? row.hoursPerWeek : 0) * 52.0) / 12;
          break;
        default:
          row.amount = row.incomeRate;
          break;
      }
    }
  }

  onAddIncome() {
    if (this.id === 0) {
      this._getUniqueNumber();
    }
    const incomeModel = IncomeModel.createEmpty(this.borrowerId(), this.id, 0);
    incomeModel.incomeFrequencyType = IncomeFrequencyType.NotAssigned;
    this.employmentIncomes = [...this.employmentIncomes, incomeModel];
    this.table().editingRowKeys[incomeModel[this.table().dataKey]] = true;
  }

  onRowDelete(index: number, incomeId: number) {
    this.employmentIncomes.splice(index, 1);
    this.incomeIds.push(incomeId);
  }

  onBlurEmployerName(): void {
    if (this.id === 0) {
      this._getUniqueNumber();
    }
  }

  addOrUpdate() {
    if (this.form.valid) {
      this._addUpdateIncomes();
      this._addUpdateEmployer();
    } else {
      CmgUiUtilities.markFormAsDirty(this.form);
      CmgUiUtilities.markFormAsTouched(this.form);
      this._showErrorsSummary();
    }
  }

  onCancelClicked() {
    this.id = undefined;
    this.editCancelled.emit();
  }

  private _addUpdateIncomes() {
    const activeId = this._appQuery.getActiveId() as number;
    this.incomeIds.forEach((id) => {
      this._empIncExpStore.deleteIncome(activeId, id);
    });
    this.employmentIncomes.forEach((income) => {
      this._empIncExpStore.addUpdateIncome(activeId, income.incomeId, income);
    });
  }

  private _addUpdateEmployer() {
    const updatedEmployment = EmploymentModel.createEmpty();
    updatedEmployment.employmentId = this.id;
    updatedEmployment.borrowerId = this.borrowerId();
    updatedEmployment.isSelfEmployment = this.form.get('isSelfEmployment').value;
    updatedEmployment.employmentType =
      this.form.get('employmentType').value === EmploymentStatus.SecondaryCurrent && this.form.get('isPrimary').value === true
        ? EmploymentStatus.Primary
        : this.form.get('employmentType').value;
    if (updatedEmployment.employmentType !== EmploymentStatus.Former) {
      updatedEmployment.dateTo = null;
      updatedEmployment.monthlyIncome = null;
    } else {
      updatedEmployment.monthlyIncome = this.form.get('monthlyIncome').value;
      updatedEmployment.dateTo = this.form.get('toDate').value;
    }
    updatedEmployment.dateFrom = this.form.get('fromDate').value;
    updatedEmployment.isSpecialRelationship = this.form.get('isSpecialRelationship').value;
    if (updatedEmployment.isSelfEmployment) {
      updatedEmployment.ownershipInterest = this.form.get('ownershipShare').value;
    } else {
      updatedEmployment.ownershipInterest = 0;
    }
    updatedEmployment.aptSuiteOther = this.form.get('aptSuiteOther').value;
    if (updatedEmployment.aptSuiteOther && updatedEmployment.aptSuiteOther.length > 0) {
      updatedEmployment.street = this.form.get('street').value + ' ' + this.form.get('aptSuiteOther').value;
    } else {
      updatedEmployment.street = this.form.get('street').value;
    }
    updatedEmployment.employerName = this.form.get('employerName').value;
    updatedEmployment.zip = this.form.get('zip').value;
    updatedEmployment.city = this.form.get('city').value;
    updatedEmployment.state = this.form.get('state').value;
    updatedEmployment.country = this.form.get('country').value;
    updatedEmployment.businessPhone = this.form.get('businessPhone').value;
    updatedEmployment.position = this.form.get('position').value;
    updatedEmployment.notes = this.form.get('notes').value;
    if (this.employmentType === 0) {
      updatedEmployment.monthlyIncome = this.form.get('monthlyIncome').value;
    } else {
      updatedEmployment.monthlyIncome = null;
    }

    this._empIncExpStore.updateEmploymentWithDefault(this._storeQuery.getActiveApplicationId(), this.isPrimaryBorrower(), this.id, updatedEmployment);
    this.editCancelled.emit();
  }

  private _buildTable() {
    this.employmentIncomes = [];
    this.incomeIds = [];
    this._empIncExpQuery
      .getEmploymentIncome$(this.id, this.borrowerId())
      .pipe(
        take(1),
        tap((incomes) => {
          incomes
            .filter((i) => i.incomeType !== IncomeType.Other && i.amount)
            .map((i) => {
              const income = Object.assign({}, i);
              income.hoursPerWeek = i.hoursPerWeek ? i.hoursPerWeek : null;
              income.incomeRate = i.incomeRate ? i.incomeRate : i.amount;
              income.incomeFrequencyType = i.incomeFrequencyType ? i.incomeFrequencyType : IncomeFrequencyType.Monthly;
              this.employmentIncomes = [...this.employmentIncomes, income];
            });
        }),
      )
      .subscribe();
  }

  private _getUniqueNumber() {
    if (this.form.get('isPrimary').value) {
      const entity = this._empIncExpQuery.getEntity(this._storeQuery.getActiveApplicationId()) as EmploymentIncomeExpenseModel;
      if (this.isPrimaryBorrower()) {
        this.id = entity.borrowerDefaultEmploymentId ?? -CmgUiUtilities.getUniqueNumber();
      } else {
        this.id = entity.coBorrowerDefaultEmploymentId ?? -CmgUiUtilities.getUniqueNumber();
      }
    } else {
      this.id = -CmgUiUtilities.getUniqueNumber();
    }
  }

  private _onZipcodeChanged(zipCode: string): void {
    this._subscription.add(
      this._lookupsService.getCityStateByZipCode(zipCode).subscribe((zipCodeModel: ZipcodeModel) => {
        if (zipCodeModel.zipCodeID) {
          this.form.get('city').setValue(zipCodeModel.city, { emitEvent: false, onlySelf: true });
          this.form.get('state').setValue(zipCodeModel.state, { emitEvent: false, onlySelf: true });
        }
      }),
    );
  }

  private _enableDisableForm(enable: boolean): void {
    if (enable) {
      this.form?.enable({ emitEvent: false });
      this.form?.get('isPrimary').disable({ emitEvent: false });
    } else {
      this.form?.disable({ emitEvent: false });
    }
  }

  /**
   * _onEmploymentTypeChanged is a bit convoluted b/c it has to
   * account for 2 radio buttons(Current/Previous) having 3 different states
   * of Current, Previous, && Current Primary.
   * Also, if Primary not present then make sure, the default selection is set to
   * current & Primary.
   * @param param0
   */
  private _onEmploymentTypeChanged([primaryEmploymentId, employmentType, readOnly]) {
    this.employmentType = employmentType;
    let enableIsPrimary = false;
    if (
      (!!primaryEmploymentId &&
        this.id === primaryEmploymentId &&
        (employmentType === EmploymentStatus.Primary || employmentType === EmploymentStatus.SecondaryCurrent)) ||
      (!primaryEmploymentId && this.id === 0 && employmentType === EmploymentStatus.SecondaryCurrent)
    ) {
      this.form.get('isPrimary').setValue(true, { emitEvent: false, onlySelf: true });
      enableIsPrimary = true;
    } else if (employmentType === EmploymentStatus.Former || (!!primaryEmploymentId && employmentType === EmploymentStatus.SecondaryCurrent)) {
      this.form.get('isPrimary').setValue(false, { emitEvent: false, onlySelf: true });
    }

    enableIsPrimary && !readOnly
      ? this.form.get('isPrimary').enable({ emitEvent: false, onlySelf: true })
      : this.form.get('isPrimary').disable({ emitEvent: false, onlySelf: true });
  }

  private _showErrorsSummary() {
    const errorPrefix = CmgUiUtilities.getBorrowerNameErrorPrefix(this.isPrimaryBorrower(), null, null);
    const controlsWithErrors = CmgUiUtilities.getControlsWithErrors(this.form);
    const invalidControls = CmgUiUtilities.getInvalidControlModels(controlsWithErrors);

    invalidControls.forEach((t) => {
      switch (t.formControlName) {
        case 'employerName':
          if (t.errors['required']) {
            t.invalidMessage = `${errorPrefix} Employer Name - Required`;
          }
          break;
        case 'fromDate':
          if (t.errors['required']) {
            t.invalidMessage = `${errorPrefix} Starting Date - Required`;
          }
          break;
        default:
          break;
      }
    });

    this._sessionStore.setValidationError(Cmg1003SectionTypes.employmentInformation, invalidControls);
  }

  private _onAdditionalBorrowerChanged(coBorrowerId: number) {
    if (coBorrowerId === null || this.readOnly()) {
      this._enableDisableForm(false);
    } else {
      this._enableDisableForm(true);
    }
  }

  private readonly _initializeForm = () => {
    if (this.id === 0) {
      this.form.get('employmentType').setValue(1);
    }

    this.form.get('isSelfEmployment').setValue('', { emitEvent: false, onlySelf: true });
    this.form.get('ownershipShare').setValue('1', { emitEvent: false, onlySelf: true });
    this.form.get('isSpecialRelationship').setValue('', { emitEvent: false, onlySelf: true });
    this.form.get('employerName').setValue('', { emitEvent: false, onlySelf: true });
    this.form.get('fromDate').setValue(null, { emitEvent: false, onlySelf: true });
    this.form.get('toDate').setValue(null, { emitEvent: false, onlySelf: true });
    this.form.get('zip').setValue('', { emitEvent: false, onlySelf: true });
    this.form.get('street').setValue('', { emitEvent: false, onlySelf: true });
    this.form.get('city').setValue('', { emitEvent: false, onlySelf: true });
    this.form.get('state').setValue('', { emitEvent: false, onlySelf: true });
    this.form.get('country').setValue('', { emitEvent: false, onlySelf: true });
    this.form.get('businessPhone').setValue('', { emitEvent: false, onlySelf: true });
    this.form.get('position').setValue('', { emitEvent: false, onlySelf: true });
    this.form.get('notes').setValue('');
    this.form.get('position').setValue('');
    this.form.get('aptSuiteOther').setValue('');
    this.form.get('monthlyIncome').setValue(null);

    if (this.id) {
      const employmentModel = this._empIncExpQuery.getEmploymentById(this.id, this.isPrimaryBorrower());
      let employmentType = EmploymentStatus.SecondaryCurrent;

      if (employmentModel.employmentType === EmploymentStatus.Primary) {
        employmentType = EmploymentStatus.SecondaryCurrent;
        if (this.isPrimaryBorrower()) {
          this.form.get('isPrimary').setValue(true, { emitEvent: false, onlySelf: true });
        }
      } else {
        employmentType = employmentModel.employmentType;
        this.form.get('isPrimary').setValue(false, { emitEvent: false, onlySelf: true });
      }

      this.form.get('employmentType').setValue(employmentType);
      this.form.get('isSelfEmployment').setValue(employmentModel.isSelfEmployment, { emitEvent: false, onlySelf: true });
      this.form.get('ownershipShare').setValue(employmentModel.ownershipInterest, { emitEvent: false, onlySelf: true });
      this.form.get('isSpecialRelationship').setValue(employmentModel.isSpecialRelationship, { emitEvent: false, onlySelf: true });
      this.form.get('employerName').setValue(employmentModel.employerName, { emitEvent: false, onlySelf: true });
      this.form.get('fromDate').setValue(!employmentModel.dateFrom ? null : new Date(employmentModel.dateFrom), { emitEvent: false, onlySelf: true });
      this.form.get('toDate').setValue(!employmentModel.dateTo ? null : new Date(employmentModel.dateTo), { emitEvent: false, onlySelf: true });
      this.form.get('zip').setValue(employmentModel.zip, { emitEvent: false, onlySelf: true });
      this.form.get('street').setValue(employmentModel?.street.trim(), { emitEvent: false, onlySelf: true });
      this.form.get('city').setValue(employmentModel.city, { emitEvent: false, onlySelf: true });
      this.form.get('state').setValue(employmentModel.state, { emitEvent: false, onlySelf: true });
      this.form.get('country').setValue(employmentModel.country, { emitEvent: false, onlySelf: true });
      this.form.get('businessPhone').setValue(employmentModel.businessPhone, { emitEvent: false, onlySelf: true });
      this.form.get('position').setValue(employmentModel.position, { emitEvent: false, onlySelf: true });
      this.form.get('notes').setValue(employmentModel.notes, { emitEvent: false, onlySelf: true });
      this.form.get('aptSuiteOther').setValue(employmentModel?.aptSuiteOther?.trim(), { emitEvent: false, onlySelf: true });
      this.form.get('monthlyIncome').setValue(employmentModel.monthlyIncome, { emitEvent: false, onlySelf: true });

      this.employmentType = employmentType;
      return employmentType;
    }
  };
}
